import { useState } from "react";
import type { FormEvent } from "react";

import { useDialogForm, useDialogState } from "@/ds";
import { validateJanelaOrder } from "@/modules/recursos";
import type { JanelaBlocoValues } from "@/modules/recursos";

import type { JanelaTipo } from "../janela-bloco";
import {
  PAPEL_EDIT_FORM_TITLE_PREFIX,
  PAPEL_FORM_TITLE,
  PAPEL_NAME_LOCKED_HINT,
} from "./papel-obra-form-dialog.constants";
import {
  toFormState,
  toPapelObraFormValues,
  validatePapelObraForm,
} from "./papel-obra-form-dialog.utils";
import type {
  PapelObraFormDialogProps,
  PapelObraFormDialogViewModel,
  PapelObraFormErrors,
  PapelObraFormField,
  PapelObraFormState,
} from "./types-papel-obra-form-dialog";

/** Liga o botão Salvar, que vive no rodapé do Dialog, ao formulário do corpo. */
const FORM_ID = "papel-obra-form";

/**
 * Formulário de papel (HU 2034 e 2035): validação em tela, janela só para papel obrigatório e RN06 recalculada a cada
 * alteração. Com alterações não salvas, Cancelar, o X e o Esc pedem a confirmação do descarte.
 */
export function usePapelObraFormDialog({
  papel,
  onSubmit,
  onCancel,
}: PapelObraFormDialogProps): PapelObraFormDialogViewModel {
  const nameLocked = papel !== undefined && papel.aplicado;
  const [serverErrors, setServerErrors] = useState<PapelObraFormErrors>({});
  const [touched, setTouched] = useState<ReadonlySet<PapelObraFormField>>(new Set());
  const descarte = useDialogState();

  const form = useDialogForm<PapelObraFormState>({
    initial: () => toFormState(papel),
    validate: validatePapelObraForm,
    onSubmit: async (state) => {
      const errors = await onSubmit(toPapelObraFormValues(state));
      if (errors) setServerErrors(errors);
    },
  });

  const { values } = form;
  const found = validatePapelObraForm(values);
  const touch = (field: PapelObraFormField) => setTouched((current) => new Set(current).add(field));

  // RN06 é recalculada a cada alteração; o serviço também pode devolver o erro.
  const orderError = values.obrigatorio
    ? validateJanelaOrder({ entrada: values.entrada, saida: values.saida })
    : null;
  const janelaError =
    orderError ?? (values.obrigatorio ? (serverErrors.janelaError ?? null) : null);

  const fieldError = (field: PapelObraFormField) =>
    form.errors[field] ?? (touched.has(field) ? found[field] : undefined) ?? null;

  function janelaProps(tipo: JanelaTipo): PapelObraFormDialogViewModel["entrada"] {
    const bloco = values[tipo];
    // O erro de ordem pinta o bloco que o usuário deve revisar: a saída, ou a entrada se a saída é fixa no término.
    const invalid =
      Boolean(janelaError) && (tipo === "entrada" ? values.saida.marcado : !values.saida.marcado);

    return {
      tipo,
      values: bloco,
      valorError: fieldError(tipo),
      invalid,
      disabled: form.isSubmitting,
      onChange: (patch: Partial<JanelaBlocoValues>) => {
        setServerErrors((current) => ({ ...current, janelaError: null }));
        form.setValue(tipo, { ...bloco, ...patch });
      },
      onValorBlur: () => touch(tipo),
    };
  }

  function cancelar() {
    if (form.isDirty) descarte.open();
    else onCancel();
  }

  return {
    titulo: papel ? `${PAPEL_EDIT_FORM_TITLE_PREFIX} ${papel.nome}` : PAPEL_FORM_TITLE,
    formId: FORM_ID,
    nome: {
      value: values.nome,
      error: serverErrors.nameError ?? fieldError("nome"),
      readOnly: nameLocked,
      hint: nameLocked ? PAPEL_NAME_LOCKED_HINT : undefined,
      digitar: (texto) => {
        setServerErrors((current) => ({ ...current, nameError: null }));
        form.setValue("nome", texto);
      },
      sairDo: () => touch("nome"),
    },
    obrigatorio: {
      value: values.obrigatorio,
      escolher: (valor) => {
        if (typeof valor !== "boolean") return;
        setServerErrors((current) => ({ ...current, janelaError: null }));
        form.setValue("obrigatorio", valor);
      },
    },
    mostrarJanela: values.obrigatorio,
    entrada: janelaProps("entrada"),
    saida: janelaProps("saida"),
    janelaError,
    isSubmitting: form.isSubmitting,
    salvarDesabilitado: Boolean(orderError),
    enviar: (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (orderError) return;

      setTouched(new Set<PapelObraFormField>(["nome", "entrada", "saida"]));
      setServerErrors({});
      void form.submit();
    },
    cancelar,
    descarte: {
      aberto: descarte.isOpen,
      confirmar: onCancel,
      continuar: descarte.close,
    },
  };
}
